import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { UserPreferencesActions } from './user-preferences.actions';
import {
  selectUserPreferences,
  selectUserPreferencesLoading,
  selectUserPreferencesError,
  selectIsPreferencesLoaded,
  selectPreferredLanguage,
  selectActiveAvatarId,
  selectAvatarVoiceSpeed,
  selectOnboardingState,
  selectUiPreferences,
  selectIsPreferencesCacheValid
} from './user-preferences.selectors';
import {
  UserPreferencesSchema,
  UserPreferencesUpdateInput,
  OnboardingStateUpdateInput,
  UiPreferencesUpdateInput
} from '@app/core/api/user-preferences';

/**
 * Facade service for user preferences state management
 * Provides clean API for components to interact with user preferences NgRx store
 */
@Injectable({
  providedIn: 'root'
})
export class UserPreferencesFacade {
  private readonly store = inject(Store);

  // Observables
  readonly preferences$ = this.store.select(selectUserPreferences);
  readonly loading$ = this.store.select(selectUserPreferencesLoading);
  readonly error$ = this.store.select(selectUserPreferencesError);
  readonly isPreferencesLoaded$ = this.store.select(selectIsPreferencesLoaded);
  readonly preferredLanguage$ = this.store.select(selectPreferredLanguage);
  readonly activeAvatarId$ = this.store.select(selectActiveAvatarId);
  readonly avatarVoiceSpeed$ = this.store.select(selectAvatarVoiceSpeed);
  readonly onboardingState$ = this.store.select(selectOnboardingState);
  readonly uiPreferences$ = this.store.select(selectUiPreferences);
  readonly isCacheValid$ = this.store.select(selectIsPreferencesCacheValid);

  // Actions
  loadPreferences(): void {
    this.store.dispatch(UserPreferencesActions.loadPreferences());
  }

  updatePreferences(data: UserPreferencesUpdateInput): void {
    this.store.dispatch(UserPreferencesActions.updatePreferences({ data }));
  }

  updateOnboardingState(data: OnboardingStateUpdateInput): void {
    this.store.dispatch(UserPreferencesActions.updateOnboardingState({ data }));
  }

  updateUiPreferences(data: UiPreferencesUpdateInput): void {
    this.store.dispatch(UserPreferencesActions.updateUiPreferences({ data }));
  }

  setPreferences(preferences: UserPreferencesSchema): void {
    this.store.dispatch(UserPreferencesActions.setPreferences({ preferences }));
  }

  clearPreferences(): void {
    this.store.dispatch(UserPreferencesActions.clearPreferences());
  }

  invalidateCache(): void {
    this.store.dispatch(UserPreferencesActions.invalidateCache());
  }

  // Helper methods
  waitForPreferences(): Observable<UserPreferencesSchema> {
    return this.preferences$.pipe(
      filter(preferences => preferences !== null),
      take(1)
    ) as Observable<UserPreferencesSchema>;
  }

  getPreferencesSnapshot(): UserPreferencesSchema | null {
    let preferences: UserPreferencesSchema | null = null;
    this.preferences$.pipe(take(1)).subscribe(p => preferences = p);
    return preferences;
  }
}
